import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit as qLimit,
  startAfter,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";

/* ============ Settings ============ */
const UI_CURRENCY = "LKR";
const UI_LOCALE = "en-LK";
const PAGE_SIZE = 400; // initial fetch size (client-side filtered)

/* ============ Money & Dates ============ */
const money = (n) =>
  `${UI_CURRENCY} ${Number(n || 0).toLocaleString(UI_LOCALE)}`;
const asDate = (ts) => {
  try {
    if (!ts) return "—";
    if (ts.toDate) return ts.toDate().toLocaleString();
    if (typeof ts === "number") return new Date(ts).toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
    return String(ts);
  } catch {
    return "—";
  }
};
const toDateOnly = (ts) => {
  const d =
    ts?.toDate?.() ??
    (typeof ts === "number" ? new Date(ts) : ts instanceof Date ? ts : null);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/* ============ Resolve People (buyers, instructors, referrers) ============ */
const personCache = new Map();

async function getUser(uid) {
  if (!uid) return null;
  try {
    const s = await getDoc(doc(db, "users", uid));
    return s.exists() ? { id: uid, ...s.data() } : null;
  } catch {
    return null;
  }
}
async function getReferralUser(uid) {
  if (!uid) return null;
  try {
    const s1 = await getDoc(doc(db, "referral_users", uid));
    if (s1.exists()) return { id: s1.id, ...s1.data() };
    const qs = await getDocs(
      query(
        collection(db, "referral_users"),
        where("uid", "==", uid),
        qLimit(1),
      ),
    );
    if (!qs.empty) {
      const d = qs.docs[0];
      return { id: d.id, ...d.data() };
    }
    return null;
  } catch {
    return null;
  }
}
const pickName = (u) =>
  u?.fullName ||
  u?.name ||
  u?.displayName ||
  u?.username ||
  u?.profile?.fullName ||
  u?.profile?.name ||
  u?.email ||
  u?.contactEmail ||
  null;
const pickEmail = (u) =>
  u?.checkoutProfile?.email ||
  u?.email ||
  u?.contactEmail ||
  u?.profile?.email ||
  null;
function pickPhone(u) {
  if (!u) return null;
  return (
    u.checkoutProfile?.phone ||
    u.whatsappNumber ||
    u.whatsAppNumber ||
    u.whatsApp ||
    u.whatsapp ||
    u.phoneNumber ||
    u.mobile ||
    u.phone ||
    null
  );
}

function pickAddress(u) {
  if (!u) return null;
  const cp = u.checkoutProfile || {};
  // try a single-line address first
  const single =
    cp.address || u.address || u.profile?.address || u.contactAddress || null;
  if (single) return String(single);
  // otherwise, stitch fields if present
  const parts = [
    cp.addressLine1,
    cp.addressLine2,
    cp.city,
    cp.state,
    cp.postalCode,
    cp.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}
async function resolvePerson(uid) {
  if (!uid) return { name: "—" };
  if (personCache.has(uid)) return personCache.get(uid);
  const [ru, us] = await Promise.all([getReferralUser(uid), getUser(uid)]);
  const person = {
    id: uid,
    name: pickName(ru) || pickName(us) || "—",
    email: pickEmail(ru) || pickEmail(us) || null,
    phone: pickPhone(ru) || pickPhone(us) || null,
    address: pickAddress(us) || pickAddress(ru) || null,
  };
  personCache.set(uid, person);
  return person;
}
const personOf = (uid) => personCache.get(uid) || { name: "—" };
const nameOf = (uid) => personOf(uid).name || "—";

function getReferrerUidFromOrder(o) {
  return (
    (o?.referrerUserId && String(o.referrerUserId)) ||
    (o?.refAttribution?.refUid && String(o.refAttribution.refUid)) ||
    (Array.isArray(o?.uplineChain) && o.uplineChain[0]?.uid) ||
    null
  );
}

/* ============ Small UI Primitives ============ */
const Section = ({ title, children, right }) => (
  <div className="bg-white rounded-2xl shadow border overflow-hidden">
    <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
      <div className="font-semibold">{title}</div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);
const Stat = ({ label, value }) => (
  <div className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-lg font-bold">{value}</div>
  </div>
);
const Chip = ({ children }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
    {children}
  </span>
);

/* ============ Simple SVG Charts ============ */
function BarChart({ data, width = 900, height = 220, pad = 24 }) {
  // data: [{label, value}]
  const max = Math.max(1, ...data.map((d) => d.value || 0));
  const bw = (width - pad * 2) / Math.max(1, data.length);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      {/* axis */}
      <line
        x1={pad}
        y1={height - pad}
        x2={width - pad}
        y2={height - pad}
        stroke="#e5e7eb"
      />
      {data.map((d, i) => {
        const h = ((d.value || 0) / max) * (height - pad * 2);
        return (
          <g key={i} transform={`translate(${pad + i * bw},0)`}>
            <rect
              x={bw * 0.15}
              y={height - pad - h}
              width={bw * 0.7}
              height={h}
              rx="8"
              fill="#6366f1"
              opacity="0.85"
            />
            <text
              x={bw * 0.5}
              y={height - pad + 12}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ points, width = 900, height = 220, pad = 24 }) {
  // points: [{xLabel, y}] equally spaced
  const max = Math.max(1, ...points.map((p) => p.y || 0));
  const step = (width - pad * 2) / Math.max(1, points.length - 1);
  const path = points
    .map((p, i) => {
      const x = pad + step * i;
      const y = height - pad - ((p.y || 0) / max) * (height - pad * 2);
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      <line
        x1={pad}
        y1={height - pad}
        x2={width - pad}
        y2={height - pad}
        stroke="#e5e7eb"
      />
      <path d={path} fill="none" stroke="#22c55e" strokeWidth="2.5" />
      {points.map((p, i) => {
        const x = pad + step * i;
        const y = height - pad - ((p.y || 0) / max) * (height - pad * 2);
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill="#22c55e" />
            <text
              x={x}
              y={height - 6}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {p.xLabel}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ============ Page ============ */
export default function BuyerInsights() {
  const [orders, setOrders] = useState([]);
  const [endCursor, setEndCursor] = useState(null);
  const [loading, setLoading] = useState(true);

  /* Filters */
  const [qType, setQType] = useState("all"); // all | course | product
  const [qCourse, setQCourse] = useState("");
  const [qProduct, setQProduct] = useState("");
  const [qPhone, setQPhone] = useState("");
  const [qEmail, setQEmail] = useState("");
  const [qBuyerName, setQBuyerName] = useState("");
  const [qInstructorName, setQInstructorName] = useState("");
  const [qRefName, setQRefName] = useState("");
  const [qMin, setQMin] = useState("");
  const [qMax, setQMax] = useState("");
  const [qMethod, setQMethod] = useState(""); // payment method if available
  const [qStatus, setQStatus] = useState("paid"); // status
  const [qDateStart, setQDateStart] = useState("");
  const [qDateEnd, setQDateEnd] = useState("");

  // initial fetch (paid orders, newest first)
  useEffect(() => {
    (async () => {
      setLoading(true);
      const q1 = query(
        collection(db, "orders"),
        where("status", "==", "paid"),
        orderBy("approvedAt", "desc"),
        qLimit(PAGE_SIZE),
      );
      const snap = await getDocs(q1);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data(), _snap: d }));
      setOrders(items);
      setEndCursor(snap.docs[snap.docs.length - 1] || null);
      setLoading(false);

      // warm names cache (+ merge inline buyer objects if present)
      const uids = new Set();
      items.forEach((o) => {
        if (o.userId) uids.add(o.userId);
        if (o.instructorUserId) uids.add(o.instructorUserId);
        const r = getReferrerUidFromOrder(o);
        if (r) uids.add(r);
        (o.commissionDistribution || []).forEach(
          (d) => d.userId && uids.add(d.userId),
        );

        // Inline buyer fallback (no awaits here)
        if (o.userId && o.buyer && typeof o.buyer === "object") {
          const existing = personCache.get(o.userId) || {};
          const addrParts = [
            o.buyer.address,
            o.buyer.addressLine1,
            o.buyer.addressLine2,
            o.buyer.city,
            o.buyer.state,
            o.buyer.postalCode,
            o.buyer.country,
          ].filter(Boolean);
          personCache.set(o.userId, {
            id: o.userId,
            name: o.buyer.name || existing.name || "—",
            email: o.buyer.email || existing.email || null,
            phone:
              o.buyer.phone ||
              o.buyer.mobile ||
              o.buyer.whatsapp ||
              o.buyer.whatsApp ||
              existing.phone ||
              null,
            address:
              (addrParts.length ? addrParts.join(", ") : null) ||
              existing.address ||
              null,
          });
        }
      });
      await Promise.all([...uids].map((uid) => resolvePerson(uid)));
      // trigger re-render with names
      setOrders((s) => [...s]);
    })();
  }, []);

  const loadMore = async () => {
    if (!endCursor) return;
    setLoading(true);
    const q2 = query(
      collection(db, "orders"),
      where("status", "==", "paid"),
      orderBy("approvedAt", "desc"),
      startAfter(endCursor),
      qLimit(PAGE_SIZE),
    );
    const snap = await getDocs(q2);
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data(), _snap: d }));
    setOrders((s) => [...s, ...items]);
    setEndCursor(snap.docs[snap.docs.length - 1] || null);
    setLoading(false);

    const uids = new Set();
    items.forEach((o) => {
      if (o.userId) uids.add(o.userId);
      if (o.instructorUserId) uids.add(o.instructorUserId);
      const r = getReferrerUidFromOrder(o);
      if (r) uids.add(r);
      (o.commissionDistribution || []).forEach(
        (d) => d.userId && uids.add(d.userId),
      );
    });
    await Promise.all([...uids].map((uid) => resolvePerson(uid)));
    setOrders((s) => [...s]);
  };

  /* Derived lists for filter dropdowns */
  const allCourses = useMemo(
    () =>
      Array.from(
        new Set(
          orders
            .map((o) => (o.type === "course" ? o.courseTitle || "" : ""))
            .filter(Boolean),
        ),
      ).sort(),
    [orders],
  );
  const allProducts = useMemo(
    () =>
      Array.from(
        new Set(
          orders
            .map((o) => (o.type === "product" ? o.productTitle || "" : ""))
            .filter(Boolean),
        ),
      ).sort(),
    [orders],
  );
  const paymentMethods = useMemo(
    () =>
      Array.from(
        new Set(
          orders.map((o) => o.paymentMethod || o.method || "").filter(Boolean),
        ),
      ).sort(),
    [orders],
  );

  /* Client-side filtering (fast + flexible) */
  const filtered = useMemo(() => {
    const st = qDateStart ? new Date(qDateStart) : null;
    const en = qDateEnd ? new Date(qDateEnd) : null;
    if (en) en.setHours(23, 59, 59, 999);

    const min = qMin ? Number(qMin) : null;
    const max = qMax ? Number(qMax) : null;

    const phoneMatch = (txt, p) =>
      !txt || (p || "").toLowerCase().includes(txt.toLowerCase());
    const strMatch = (txt, s) =>
      !txt || (s || "").toLowerCase().includes(txt.toLowerCase());

    return orders.filter((o) => {
      if (qStatus && o.status !== qStatus) return false;
      if (qType !== "all" && o.type !== qType) return false;

      // date
      const d = toDateOnly(o.approvedAt);
      if (st && d && d < toDateOnly(st)) return false;
      if (en && d && d > toDateOnly(en)) return false;

      // amount
      if (min != null && Number(o.amount || 0) < min) return false;
      if (max != null && Number(o.amount || 0) > max) return false;

      // course/product text filters
      if (
        qCourse &&
        (o.courseTitle || "").toLowerCase() !== qCourse.toLowerCase()
      )
        return false;
      if (
        qProduct &&
        (o.productTitle || "").toLowerCase() !== qProduct.toLowerCase()
      )
        return false;

      // payment method
      if (qMethod && (o.paymentMethod || o.method) !== qMethod) return false;

      // people
      const buyer = personOf(o.userId);
      const inst = personOf(o.instructorUserId);
      const ref = personOf(getReferrerUidFromOrder(o));

      if (qPhone && !phoneMatch(qPhone, buyer.phone)) return false;
      if (qEmail && !strMatch(qEmail, buyer.email)) return false;
      if (qBuyerName && !strMatch(qBuyerName, buyer.name)) return false;
      if (qInstructorName && !strMatch(qInstructorName, inst.name))
        return false;
      if (qRefName && !strMatch(qRefName, ref.name)) return false;

      return true;
    });
  }, [
    orders,
    qStatus,
    qType,
    qCourse,
    qProduct,
    qMethod,
    qPhone,
    qEmail,
    qBuyerName,
    qInstructorName,
    qRefName,
    qDateStart,
    qDateEnd,
    qMin,
    qMax,
  ]);

  /* Aggregations */
  const userSummary = useMemo(() => {
    const map = new Map();
    filtered.forEach((o) => {
      const uid = o.userId || "unknown";
      const entry = map.get(uid) || {
        userId: uid,
        name: nameOf(uid),
        email: personOf(uid).email || "",
        phone: personOf(uid).phone || "",
        orders: 0,
        total: 0,
        first: null,
        last: null,
        items: [], // what they bought
      };
      entry.orders += 1;
      entry.total += Number(o.amount || 0);
      const d =
        o.approvedAt?.toDate?.() ??
        (typeof o.approvedAt === "number" ? new Date(o.approvedAt) : null);
      if (d) {
        if (!entry.first || d < entry.first) entry.first = d;
        if (!entry.last || d > entry.last) entry.last = d;
      }
      entry.items.push({
        type: o.type,
        title:
          o.courseTitle ||
          o.productTitle ||
          o.course?.title ||
          o.product?.title ||
          "Item",
        at: d,
        amount: Number(o.amount || 0),
      });
      map.set(uid, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filtered]);

  const purchasesByMonth = useMemo(() => {
    // last 12 months
    const buckets = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString(UI_LOCALE, { month: "short" }),
        year: d.getFullYear(),
        count: 0,
      });
    }
    const idxByKey = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
    filtered.forEach((o) => {
      const d =
        o.approvedAt?.toDate?.() ??
        (typeof o.approvedAt === "number" ? new Date(o.approvedAt) : null);
      if (!d) return;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}`;
      const idx = idxByKey[k];
      if (idx != null) buckets[idx].count++;
    });
    return buckets.map((b) => ({ xLabel: `${b.label}`, y: b.count }));
  }, [filtered]);

  const topItems = useMemo(() => {
    const map = new Map();
    filtered.forEach((o) => {
      const key =
        (o.type === "course" ? o.courseTitle : o.productTitle) ||
        o.course?.title ||
        o.product?.title ||
        "Item";
      map.set(key, (map.get(key) || 0) + 1);
    });
    const arr = Array.from(map.entries())
      .map(([label, value]) => ({
        label: label.slice(0, 10) + (label.length > 10 ? "…" : ""),
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    return arr;
  }, [filtered]);

  /* Downloads */
  const downloadJSON = (all = false) => {
    const data = (all ? orders : filtered).map((o) => ({
      id: o.id,
      status: o.status,
      approvedAt: asDate(o.approvedAt),
      type: o.type,
      title:
        o.courseTitle ||
        o.productTitle ||
        o.course?.title ||
        o.product?.title ||
        "",
      amount: Number(o.amount || 0),
      buyer: { ...personOf(o.userId) }, // now includes address
      instructor: personOf(o.instructorUserId),
      referrer: personOf(getReferrerUidFromOrder(o)),
      method: o.paymentMethod || o.method || "",
      currency: UI_CURRENCY,
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `buyer_insights_${
      all ? "all" : "filtered"
    }_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV = (all = false) => {
    const data = all ? orders : filtered;
    const rows = [
      [
        "Order ID",
        "Approved At",
        "Status",
        "Type",
        "Title",
        "Amount",
        "Buyer",
        "Buyer Email",
        "Buyer Phone",
        "Buyer Address",
        "Instructor",
        "Referrer",
        "Payment Method",
        "Currency",
      ],
    ];
    data.forEach((o) => {
      const buyer = personOf(o.userId);
      const inst = personOf(o.instructorUserId);
      const ref = personOf(getReferrerUidFromOrder(o));
      rows.push([
        o.id,
        asDate(o.approvedAt),
        o.status || "",
        o.type || "",
        o.courseTitle ||
          o.productTitle ||
          o.course?.title ||
          o.product?.title ||
          "",
        Number(o.amount || 0),
        buyer.name || "",
        buyer.email || "",
        buyer.phone || "",
        buyer.address || "",
        inst.name || "",
        ref.name || "",
        o.paymentMethod || o.method || "",
        UI_CURRENCY,
      ]);
    });
    const csv = rows
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? "");
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `buyer_insights_${all ? "all" : "filtered"}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">
            Buyer Insights
          </h1>
          <div className="text-gray-500 text-sm mt-1">
            Visualize buyer activity, filter deeply, and export your data.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadCSV(false)}
            className="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold shadow hover:opacity-90 disabled:opacity-40"
            disabled={loading || filtered.length === 0}
            title="Download filtered as CSV"
          >
            Download CSV (filtered)
          </button>
          <button
            onClick={() => downloadJSON(false)}
            className="px-3 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm shadow hover:bg-gray-200 disabled:opacity-40"
            disabled={loading || filtered.length === 0}
            title="Download filtered as JSON"
          >
            JSON
          </button>
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button
            onClick={() => downloadCSV(true)}
            className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow hover:bg-blue-700 disabled:opacity-40"
            disabled={loading || orders.length === 0}
            title="Download all as CSV"
          >
            Download CSV (all)
          </button>
          <button
            onClick={() => downloadJSON(true)}
            className="px-3 py-2 rounded-xl bg-blue-50 text-blue-800 text-sm shadow hover:bg-blue-100 disabled:opacity-40"
            disabled={loading || orders.length === 0}
            title="Download all as JSON"
          >
            JSON
          </button>
        </div>
      </div>

      {/* Filter panel */}
      <Section
        title="Filters"
        right={
          <Chip>
            {filtered.length} / {orders.length} orders
          </Chip>
        }
      >
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-600">Type</label>
            <select
              value={qType}
              onChange={(e) => setQType(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2 bg-white"
            >
              <option value="all">All</option>
              <option value="course">Course</option>
              <option value="product">Product</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Course name</label>
            <input
              list="courseOptions"
              value={qCourse}
              onChange={(e) => setQCourse(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="Select or type…"
            />
            <datalist id="courseOptions">
              {allCourses.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="text-xs text-gray-600">Product name</label>
            <input
              list="productOptions"
              value={qProduct}
              onChange={(e) => setQProduct(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="Select or type…"
            />
            <datalist id="productOptions">
              {allProducts.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="text-xs text-gray-600">Payment method</label>
            <select
              value={qMethod}
              onChange={(e) => setQMethod(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Any</option>
              {paymentMethods.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Buyer mobile</label>
            <input
              value={qPhone}
              onChange={(e) => setQPhone(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="+94 7X…"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Buyer email</label>
            <input
              value={qEmail}
              onChange={(e) => setQEmail(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="name@email.com"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Buyer name</label>
            <input
              value={qBuyerName}
              onChange={(e) => setQBuyerName(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="e.g. Nuwan"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Instructor name</label>
            <input
              value={qInstructorName}
              onChange={(e) => setQInstructorName(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="e.g. Charitha"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Referrer name</label>
            <input
              value={qRefName}
              onChange={(e) => setQRefName(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="Affiliate user"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Min amount</label>
            <input
              type="number"
              value={qMin}
              onChange={(e) => setQMin(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="0"
              min="0"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Max amount</label>
            <input
              type="number"
              value={qMax}
              onChange={(e) => setQMax(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
              placeholder="999999"
              min="0"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">From date</label>
            <input
              type="date"
              value={qDateStart}
              onChange={(e) => setQDateStart(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">To date</label>
            <input
              type="date"
              value={qDateEnd}
              onChange={(e) => setQDateEnd(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Status</label>
            <select
              value={qStatus}
              onChange={(e) => setQStatus(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2 bg-white"
            >
              <option value="paid">Paid</option>
              <option value="">Any</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={() => {
                setQType("all");
                setQCourse("");
                setQProduct("");
                setQMethod("");
                setQPhone("");
                setQEmail("");
                setQBuyerName("");
                setQInstructorName("");
                setQRefName("");
                setQMin("");
                setQMax("");
                setQDateStart("");
                setQDateEnd("");
                setQStatus("paid");
              }}
              className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200"
            >
              Reset
            </button>
            <button
              onClick={loadMore}
              disabled={!endCursor || loading}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
              title="Load more orders from Firestore"
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          </div>
        </div>
      </Section>

      {/* KPIs */}
      <div className="grid md:grid-cols-3 gap-6 my-6">
        <Stat label="Filtered orders" value={filtered.length} />
        <Stat
          label="Filtered revenue"
          value={money(filtered.reduce((t, o) => t + Number(o.amount || 0), 0))}
        />
        <Stat label="Unique buyers" value={userSummary.length} />
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <Section title="Purchases (last 12 months)">
          <LineChart points={purchasesByMonth} />
        </Section>
        <Section title="Top items (by order count)">
          <BarChart data={topItems} />
        </Section>
      </div>

      {/* Buyers table */}
      <Section title="Buyers" right={<Chip>{userSummary.length} unique</Chip>}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <Th w="18rem">Buyer</Th>
                <Th w="10rem">Email</Th>
                <Th w="10rem">Phone</Th>
                <Th w="14rem">Address</Th>
                <Th w="8rem" right>
                  Total Orders
                </Th>
                <Th w="10rem" right>
                  Spent
                </Th>
                <Th w="10rem">First</Th>
                <Th w="10rem">Last</Th>
                <Th w="6rem"></Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {userSummary.map((u) => (
                <tr key={u.userId} className="hover:bg-gray-50/70">
                  <Td>
                    <div className="font-medium truncate">{u.name || "—"}</div>
                  </Td>
                  <Td title={u.email}>{u.email || "—"}</Td>
                  <Td title={u.phone}>{u.phone || "—"}</Td>
                  <Td title={personOf(u.userId)?.address}>
                    {personOf(u.userId)?.address || "—"}
                  </Td>
                  <Td right>{u.orders}</Td>
                  <Td right>{money(u.total)}</Td>
                  <Td>{u.first ? u.first.toLocaleDateString() : "—"}</Td>
                  <Td>{u.last ? u.last.toLocaleDateString() : "—"}</Td>
                  <Td>
                    <BuyerItemsModalButton items={u.items} buyer={u} />
                  </Td>
                </tr>
              ))}
              {userSummary.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-gray-500">
                    No buyers in current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

/* ====== Table helpers ====== */
function Th({ children, w, right }) {
  return (
    <th
      className={`px-4 py-2 text-left text-[12px] font-semibold tracking-wide ${
        right ? "text-right" : ""
      }`}
      style={w ? { width: w } : {}}
    >
      {children}
    </th>
  );
}
function Td({ children, right, className = "", title }) {
  return (
    <td
      title={title}
      className={`px-4 py-3 align-top ${
        right ? "text-right" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}

/* ====== Buyer Items modal button + modal ====== */
function BuyerItemsModalButton({ items, buyer }) {
  const [open, setOpen] = useState(false);

  const downloadItemsCSV = () => {
    const rows = [["Type", "Title", "When", "Amount", "Currency"]];
    items.forEach((it) =>
      rows.push([
        it.type || "",
        it.title || "",
        it.at ? it.at.toLocaleString() : "",
        Number(it.amount || 0),
        UI_CURRENCY,
      ]),
    );
    const csv = rows
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? "");
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `buyer_${(buyer?.name || "user").replace(
      /\s+/g,
      "_",
    )}_items_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button
        className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs shadow hover:bg-blue-700"
        onClick={() => setOpen(true)}
      >
        View
      </button>

      {open && (
        <div className="fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          {/* Modal container: flex column + max height + scrollable body */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-[95%] max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header (fixed) */}
            <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between shrink-0">
              <div className="font-semibold">
                Purchases — {buyer?.name || "Buyer"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded-lg bg-gray-900 text-white text-xs shadow hover:opacity-90"
                  onClick={downloadItemsCSV}
                >
                  Download CSV
                </button>
                <button
                  className="px-2 py-1 rounded-lg hover:bg-gray-100"
                  onClick={() => setOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body (scrollable) */}
            <div className="p-4 overflow-y-auto grow">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <Th>Type</Th>
                      <Th>Title</Th>
                      <Th>When</Th>
                      <Th right>Amount</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((it, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/70">
                        <Td>{it.type}</Td>
                        <Td title={it.title}>
                          <div className="truncate max-w-[420px]">
                            {it.title}
                          </div>
                        </Td>
                        <Td>{it.at ? it.at.toLocaleString() : "—"}</Td>
                        <Td right>{money(it.amount)}</Td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-6 text-center text-gray-500"
                        >
                          No items.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Optional footer could go here (shrink-0) */}
          </div>
        </div>
      )}
    </>
  );
}
